import dayjs from 'dayjs'
import { computed } from 'vue'

// ─── Pure utilities ────────────────────────────────────────────────────────────

/**
 * Convert YYYY-MM-DD → YYYY/MM/DD for q-date model-value
 */
export const dateToPickerFormat = (dateStr) =>
  dateStr ? dateStr.replace(/-/g, '/') : null

/**
 * Convert YYYY/MM/DD → YYYY-MM-DD for backend storage
 */
export const pickerToDateFormat = (val) =>
  val ? val.replace(/\//g, '-') : null

/**
 * Extract HH:mm from an ISO datetime string
 */
export const isoToTime = (isoStr) => {
  if (!isoStr) return null
  const d = dayjs(isoStr)
  return d.isValid() ? d.format('HH:mm') : null
}

/**
 * Extract YYYY-MM-DD from an ISO datetime string
 */
export const isoToDate = (isoStr) => {
  if (!isoStr) return null
  const d = dayjs(isoStr)
  return d.isValid() ? d.format('YYYY-MM-DD') : null
}

/**
 * Build ISO string from a YYYY-MM-DD date and HH:mm time string.
 * Falls back to today if dateStr is null.
 */
export const buildTimeISO = (dateStr, timeStr) => {
  if (!timeStr) return null
  const base = dateStr || dayjs().format('YYYY-MM-DD')
  return dayjs(`${base}T${timeStr}`).toISOString()
}

/**
 * Replace only the time portion of referenceISO using a HH:mm string.
 * Preserves the date from referenceISO (or uses today).
 */
export const replaceTimeInISO = (referenceISO, timeStr) => {
  if (!timeStr) return null
  const base = referenceISO ? dayjs(referenceISO) : dayjs()
  const [h, m] = timeStr.split(':')
  return base
    .set('hour', parseInt(h))
    .set('minute', parseInt(m))
    .set('second', 0)
    .set('millisecond', 0)
    .toISOString()
}

/**
 * Convert start/end ISO strings to q-date range model value.
 * Returns a plain string when start and end fall on the same day.
 */
export const isoRangeToQDate = (startISO, endISO) => {
  if (!startISO) return null
  const start = dayjs(startISO)
  const end = dayjs(endISO)
  if (start.isSame(end, 'day')) return start.format('YYYY/MM/DD')
  return {
    from: start.format('YYYY/MM/DD'),
    to: end.format('YYYY/MM/DD'),
  }
}

/**
 * Apply a q-date range value back to ISO strings, preserving original times.
 * @param val — q-date emitted value (string | { from, to } | null)
 * @param currentStartISO — current start ISO (to extract preserved time)
 * @param currentEndISO  — current end ISO (to extract preserved time)
 * @returns { start: string, end: string }
 */
export const applyQDateRange = (val, currentStartISO, currentEndISO) => {
  const startTime = dayjs(currentStartISO).format('HH:mm')
  const endTime = dayjs(currentEndISO).format('HH:mm')

  let startDay, endDay
  if (!val) {
    startDay = endDay = dayjs()
  } else if (typeof val === 'object') {
    startDay = dayjs(val.from)
    endDay = dayjs(val.to)
  } else {
    startDay = endDay = dayjs(val)
  }

  const [sh, sm] = startTime.split(':')
  const [eh, em] = endTime.split(':')

  return {
    start: startDay.set('hour', parseInt(sh)).set('minute', parseInt(sm)).toISOString(),
    end: endDay.set('hour', parseInt(eh)).set('minute', parseInt(em)).toISOString(),
  }
}

// ─── Composables ───────────────────────────────────────────────────────────────

/**
 * Manages a single date field (YYYY-MM-DD) for q-date usage.
 *
 * @param editItem — reactive ref to the entity being edited
 * @param dateField — name of the date field (default: 'date')
 * @returns { dateForPicker, onUpdateDate }
 */
export const useDateField = (editItem, dateField = 'date') => {
  const dateForPicker = computed(() =>
    dateToPickerFormat(editItem.value[dateField])
  )

  const onUpdateDate = (val) => {
    editItem.value[dateField] = pickerToDateFormat(val)
  }

  return { dateForPicker, onUpdateDate }
}

/**
 * Manages HH:mm time fields against a date field, bridging to ISO for TimeSelector.
 *
 * @param editItem — reactive ref to the entity being edited
 * @param dateField — name of the date field to anchor times to (default: 'date')
 * @returns { baseDate, timeToISO, onUpdateTime }
 *   - baseDate: computed YYYY-MM-DD (falls back to today)
 *   - timeToISO(timeStr): converts HH:mm to ISO anchored to baseDate
 *   - onUpdateTime(field, isoVal): writes HH:mm (or null) back to editItem
 */
export const useTimeFields = (editItem, dateField = 'date') => {
  const baseDate = computed(() =>
    editItem.value[dateField] || dayjs().format('YYYY-MM-DD')
  )

  const timeToISO = (timeStr) => buildTimeISO(baseDate.value, timeStr)

  const onUpdateTime = (field, isoVal) => {
    editItem.value[field] = isoVal != null ? isoToTime(isoVal) : null
  }

  return { baseDate, timeToISO, onUpdateTime }
}

/**
 * Manages a start/end ISO date-range pair for q-date range usage,
 * preserving existing times when the date changes.
 *
 * @param editItem  — reactive ref to the entity being edited
 * @param startField — name of the start ISO field (default: 'start')
 * @param endField   — name of the end ISO field (default: 'end')
 * @returns { fromTo, onUpdateDateRange }
 */
export const useISODateRange = (editItem, startField = 'start', endField = 'end') => {
  const fromTo = computed(() =>
    isoRangeToQDate(editItem.value[startField], editItem.value[endField])
  )

  const onUpdateDateRange = (val) => {
    const result = applyQDateRange(
      val,
      editItem.value[startField],
      editItem.value[endField]
    )
    editItem.value[startField] = result.start
    editItem.value[endField] = result.end
  }

  return { fromTo, onUpdateDateRange }
}

export const formatRecentDateTime = (value) => {
  const moment = dayjs(value)
  return moment.format(moment.isSame(dayjs(), 'day') ? 'LT' : 'L LT')
}
